/**
 * PawsLoader - Beautiful, dog-friendly loading screen
 * Displays animated paws logo with rotating messages
 */

import { useState, useEffect } from "react";
import { Box, Typography, keyframes } from "@mui/material";

// Paw print SVG path
const PAW_PATH = "M12 2C9.5 2 7.5 4 7.5 6.5C7.5 9 9.5 11 12 11C14.5 11 16.5 9 16.5 6.5C16.5 4 14.5 2 12 2ZM4.5 9C2.5 9 1 10.5 1 12.5C1 14.5 2.5 16 4.5 16C6.5 16 8 14.5 8 12.5C8 10.5 6.5 9 4.5 9ZM19.5 9C17.5 9 16 10.5 16 12.5C16 14.5 17.5 16 19.5 16C21.5 16 23 14.5 23 12.5C23 10.5 21.5 9 19.5 9ZM6 17C4 17 2.5 18.5 2.5 20.5C2.5 22.5 4 24 6 24C8 24 9.5 22.5 9.5 20.5C9.5 18.5 8 17 6 17ZM18 17C16 17 14.5 18.5 14.5 20.5C14.5 22.5 16 24 18 24C20 24 21.5 22.5 21.5 20.5C21.5 18.5 20 17 18 17Z";

// Animation keyframes
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
`;

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pawStep = keyframes`
  0% {
    opacity: 0.2;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0.2;
    transform: scale(0.8);
  }
`;

// Loading messages
const LOADING_MESSAGES = [
  "Fetching your pup's plan...",
  "Preparing tasty meals...",
  "Loading your dashboard...",
  "Getting everything ready...",
  "Almost there...",
  "Sniffing out the details...",
  "Wagging our tails...",
];

// Specific messages for different actions
const ACTION_MESSAGES: Record<string, string[]> = {
  default: LOADING_MESSAGES,
  checkout: [
    "Building your box...",
    "Adding your meals...",
    "Preparing checkout...",
    "Almost ready...",
  ],
  skip: [
    "Rescheduling your delivery...",
    "Updating your order...",
    "Almost done...",
  ],
  delivery: [
    "Loading delivery dates...",
    "Checking availability...",
    "Finding the best dates...",
  ],
  saving: [
    "Saving your changes...",
    "Updating your plan...",
    "Almost there...",
  ],
  treats: [
    "Adding treats to your order...",
    "Your pup will love these...",
    "Finalizing treats...",
  ],
  subscription: [
    "Loading your subscription...",
    "Fetching plan details...",
    "Getting your meals ready...",
  ],
};

type PawsLoaderProps = {
  /** Optional message to display (overrides rotating messages) */
  message?: string;
  /** Action type for contextual messages */
  action?: keyof typeof ACTION_MESSAGES;
  /** Whether to show full screen overlay or inline */
  fullScreen?: boolean;
  /** Size variant */
  size?: "small" | "medium" | "large";
};

export function PawsLoader({
  message,
  action = "default",
  fullScreen = true,
  size = "medium",
}: PawsLoaderProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const messages = ACTION_MESSAGES[action] || ACTION_MESSAGES.default;

  // Rotate messages every 2.5 seconds
  useEffect(() => {
    if (message) return; // Don't rotate if custom message provided

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [messages, message]);

  const displayMessage = message || messages[messageIndex];

  // Size configurations
  const sizes = {
    small: {
      logoWidth: 80,
      pawSize: 16,
      fontSize: 14,
      gap: 1.5,
    },
    medium: {
      logoWidth: 120,
      pawSize: 20,
      fontSize: 16,
      gap: 2,
    },
    large: {
      logoWidth: 160,
      pawSize: 24,
      fontSize: 18,
      gap: 3,
    },
  };

  const config = sizes[size];

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: config.gap,
        p: 4,
      }}
    >
      {/* Logo with pulse animation */}
      <Box
        sx={{
          animation: `${pulse} 2s ease-in-out infinite`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src="https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Paws_on_Plates_Tertiary_Logo_Brown.svg?v=1744270930"
          alt="Paws on Plates"
          sx={{
            width: config.logoWidth,
            height: "auto",
          }}
        />
      </Box>

      {/* Animated paw prints */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          my: 1,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              animation: `${pawStep} 1.2s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          >
            <svg
              width={config.pawSize}
              height={config.pawSize}
              viewBox="0 0 24 24"
              fill="#7B3306"
            >
              <path d={PAW_PATH} />
            </svg>
          </Box>
        ))}
      </Box>

      {/* Loading message with fade animation */}
      <Typography
        key={displayMessage}
        sx={{
          fontSize: config.fontSize,
          fontWeight: 600,
          color: "#4A301E",
          textAlign: "center",
          animation: `${fadeInUp} 0.4s ease-out`,
          minHeight: 24,
        }}
      >
        {displayMessage}
      </Typography>

      {/* Subtle progress indicator */}
      <Box
        sx={{
          width: 60,
          height: 3,
          borderRadius: 1.5,
          background: "rgba(123, 51, 6, 0.15)",
          overflow: "hidden",
          mt: 1,
        }}
      >
        <Box
          sx={{
            width: "40%",
            height: "100%",
            borderRadius: 1.5,
            background: "linear-gradient(90deg, #7B3306, #FF7874)",
            "@keyframes slide": {
              "0%": { transform: "translateX(-100%)" },
              "100%": { transform: "translateX(250%)" },
            },
            animation: "slide 1.5s ease-in-out infinite",
          }}
        />
      </Box>
    </Box>
  );

  if (fullScreen) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #FAF5EB 0%, #FFF8F0 100%)",
          zIndex: 9999,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 300,
        background: "linear-gradient(180deg, #FAF5EB 0%, #FFF8F0 100%)",
        borderRadius: 3,
      }}
    >
      {content}
    </Box>
  );
}

/**
 * Simple inline loader for buttons and small areas
 */
export function PawsSpinner({ size = 20, color = "#7B3306" }: { size?: number; color?: string }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        animation: `${pulse} 1s ease-in-out infinite`,
      }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d={PAW_PATH} />
      </svg>
    </Box>
  );
}
